"use client";

import React, { useMemo, useState } from "react";

import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useTranslations } from "next-intl";

import { FAQSchema } from "./FAQSchema";
import { CloudBreak } from "./cloudBreak";

interface FAQItemProps {
    answer: string;
    isOpen: boolean;
    onClick: () => void;
    question: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ answer, isOpen, onClick, question }) => {
    return (
        <div className="border-b border-black last:border-b-0">
            <button
                className="w-full py-5 text-left flex justify-between items-center focus:outline-none"
                onClick={onClick}
                type="button"
            >
                <span
                    className={`text-xl md:text-2xl transition-colors duration-200 ${isOpen ? "text-primary-100" : "text-black"}`}
                >
                    {question}
                </span>
                <ChevronDownIcon
                    className={`w-6 h-6 text-black transition-transform duration-200 ${
                        isOpen ? "transform rotate-180" : ""
                    }`}
                />
            </button>
            <div className={`transition-all duration-200 overflow-hidden ${isOpen ? "max-h-full mb-6" : "max-h-0"}`}>
                <p className="text-black text-lg">{answer}</p>
            </div>
        </div>
    );
};

export function FAQSection({ onlyEssential }: { onlyEssential?: boolean }) {
    const [openIndex, setOpenIndex] = useState<null | number>(null);
    const t = useTranslations("NewHome.faq");
    const questions = useMemo(
        () =>
            onlyEssential
                ? [
                      "howItWorks", // Explains the process
                      "results", // Shows what they can expect
                      "safety", // Addresses safety concerns
                      "canIUse", // Addresses if it's right for them
                  ]
                : [
                      "howItWorks",
                      "results",
                      "whatIs",
                      "safety",
                      "canIUse",
                      "ageGroup",
                      "consultation",
                      "twins",
                      "teething",
                      "time",
                      "diagnoses",
                  ],
        [onlyEssential],
    );

    const faqData = useMemo(
        () =>
            questions.map((key) => ({
                answer: t(`questions.${key}.answer`),
                question: t(`questions.${key}.question`),
            })),
        [questions, t],
    );

    return (
        <section className="bg-light-100" id="faq-section">
            <FAQSchema questions={faqData} />
            <div className="container mx-auto px-4 max-w-4xl">
                <h2 className="text-3xl md:text-4xl font-bold text-center text-black mb-12">{t("title")}</h2>
                <div className="border border-black rounded-3xl py-1 px-8">
                    {questions.map((key, index) => (
                        <FAQItem
                            answer={t(`questions.${key}.answer`)}
                            isOpen={openIndex === index}
                            key={key}
                            onClick={() => setOpenIndex(openIndex === index ? null : index)}
                            question={t(`questions.${key}.question`)}
                        />
                    ))}
                </div>
            </div>
            <CloudBreak />
        </section>
    );
}
